<template>
  <div class="c-grid">
    <div class="c-grid__items">
      <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__7 c-grid__item__l__7">
        <BorderBox :title="$t('components.CompaniesSurveyTemplateForm.complete.title')">
          <MediaImage
            class="form"
            :max-files="1"
            :images="images"
            :loading="loading"
            @drop-images="onDropMediaImage"
            @remove-image="onRemoveMediaImage"
          />
          <KkTooltip :label="$t('labels.imageTooltipContent.title')"
                     top="25px"
                     width="400px"
                     :is-visible="isShowHoverImageTooltip"
                     @show-tooltip="showHoverImageTooltip"
                     @hide-tooltip="hideHoverImageTooltip"
          >
            <KkImageTooltipContent count="1" format="JPG,JPEG,PNG" width="400" height="300" size="5" :is-visible-resize="false" />
          </KkTooltip>
          <div class="form">
            <ValidationProvider
              v-slot="{ errors }"
              v-model="title"
              :name="$t('components.CompaniesSurveyTemplateForm.complete.formLabels.title')"
              :rules="'required|max:' + maxLength['title']"
            >
              <label class="c-formLabel">
                {{ labelTextFormat({ label: 'title', max: maxLength['title'], required: true }) }}
              </label>
              <TextField
                :value="title"
                class="c-textField"
                :placeholder="$t('messages.noSettings')" @input="e => changeCompleteAttribute(e.target.value, 'title')"
              />
              <ValidationError :error="errors[0]" />
            </ValidationProvider>
          </div>
          <div class="form">
            <ValidationProvider
              v-slot="{ errors }"
              v-model="text"
              :name="$t('components.CompaniesSurveyTemplateForm.complete.formLabels.text')"
              :rules="'required|max:' + maxLength['text']"
            >
              <label class="c-formLabel">
                {{ labelTextFormat({ label: 'text', max: maxLength['text'], required: true }) }}
              </label>
              <textarea
                :value="text"
                class="c-textArea"
                :placeholder="$t('messages.noSettings')"
                rows="5"
                @input="e => changeCompleteAttribute(e.target.value, 'text')"
              />
              <ValidationError :error="errors[0]" />
            </ValidationProvider>
          </div>
          <div class="form">
            <input
              id="checkbox__showBtnForm"
              :checked="showBtnForm"
              class="c-checkbox__input"
              type="checkbox"
              @change="() => changeCompleteAttribute(!showBtnForm, 'showBtnForm')"
            >
            <label class="c-checkbox__label" for="checkbox__showBtnForm">
              {{ $t('components.CompaniesSurveyTemplateForm.complete.formLabels.showBtnForm') }}
            </label>
          </div>
          <template v-if="showBtnForm">
            <div class="form">
              <ValidationProvider
                v-slot="{ errors }"
                v-model="btnLabel"
                :name="$t('components.CompaniesSurveyTemplateForm.complete.formLabels.btnLabel')"
                :rules="'required|max:' + maxLength['btnLabel']"
              >
                <label class="c-formLabel">
                  {{ labelTextFormat({ label: 'btnLabel', max: maxLength['btnLabel'], required: true }) }}
                </label>
                <TextField
                  :value="btnLabel"
                  class="c-textField"
                  :placeholder="$t('messages.noSettings')"
                  @input="e => changeCompleteAttribute(e.target.value, 'btnLabel')"
                />
                <ValidationError :error="errors[0]" />
              </ValidationProvider>
            </div>
            <div class="form">
              <ValidationProvider
                v-slot="{ errors }"
                v-model="btnLink"
                :name="$t('components.CompaniesSurveyTemplateForm.complete.formLabels.btnLink')"
                :rules="'required|max:' + maxLength['btnLink']"
              >
                <label class="c-formLabel">
                  {{ labelTextFormat({ label: 'btnLink', max: maxLength['btnLink'], required: true }) }}
                </label>
                <TextField
                  :value="btnLink"
                  class="c-textField"
                  :placeholder="$t('messages.noSettings')"
                  @input="e => changeCompleteAttribute(e.target.value, 'btnLink')"
                />
                <ValidationError :error="errors[0]" />
                <p>{{ $t('components.CompaniesSurveyTemplateForm.complete.example.btnLink') }}</p>
              </ValidationProvider>
            </div>
          </template>
        </BorderBox>
        <BorderBox
          :title="$t('components.CompaniesSurveyTemplateForm.complete.guideToReviewSite.title')"
          class="box"
        >
          <div class="form">
            <KkCheckbox
              id="guideToReviewSiteDelete"
              :label="$t('components.CompaniesSurveyTemplateForm.complete.formLabels.guideToReviewSiteDelete')"
              :model-value="!guideToReviewSiteDelete"
              @update:model-value="value => changeGuideToReviewSiteAttribute(!value, 'delete')"
            />
          </div>
          <template v-if="!guideToReviewSiteDelete">
            <div class="form">
              <ValidationProvider
                v-slot="{ errors }"
                v-model="guideToReviewSiteTitle"
                :name="$t('components.CompaniesSurveyTemplateForm.complete.formLabels.guideToReviewSiteTitleForId')"
                :rules="'required|max:' + maxLength['guideToReviewSiteTitle']"
              >
                <label class="c-formLabel">
                  {{ labelTextFormat({ label: 'guideToReviewSiteTitle', max: maxLength['guideToReviewSiteTitle'], required: true }) }}
                </label>
                <TextField
                  :value="guideToReviewSiteTitle"
                  class="c-textField"
                  :placeholder="$t('messages.noSettings')" @input="e => changeGuideToReviewSiteAttribute(e.target.value, 'title')"
                />
                <ValidationError :error="errors[0]" />
              </ValidationProvider>
            </div>
            <Info class="info">
              {{ $t('components.CompaniesSurveyTemplateForm.complete.guideToReviewSite.info') }}
            </Info>
            <div class="form">
              <KkRadioGroup
                :model-value="guideToReviewSiteDisplayLocation"
                :items="guideToReviewSiteDisplayLocationOptions"
                name="guideToReviewSiteDisplayLocation"
                @update:model-value="value => changeGuideToReviewSiteAttribute(value, 'displayLocation')"
              />
            </div>
          </template>
        </BorderBox>
      </div>
      <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__5 c-grid__item__l__5">
        <PreviewContainer :title="$t('components.CompaniesSurveyTemplateForm.complete.previewName')">
          <Loading v-if="companiesCustomSurveyLoading" />
          <SurveyCompleteFormPreview v-else :survey="companiesCustomSurvey" :no-header="true" />
        </PreviewContainer>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import BorderBox from './BorderBox'
import MediaImage from '../MediaImage'
import Info from '../Info'
import Loading from '../Loading'
import PreviewContainer from '../PreviewContainer'
import SurveyCompleteFormPreview from '../SurveyCompleteFormPreview'
import TextField from '../TextField'
import ValidationError from '../ValidationError'
import { KkRadioGroup, KkCheckbox, KkTooltip, KkImageTooltipContent } from '../../components/base'

export default {
  name: 'CompaniesSurveyTemplateFormComplete',
  components: {
    BorderBox,
    MediaImage,
    Info,
    Loading,
    PreviewContainer,
    SurveyCompleteFormPreview,
    TextField,
    ValidationError,
    KkRadioGroup,
    KkCheckbox,
    KkTooltip,
    KkImageTooltipContent,
  },
  data() {
    return {
      isShowHoverImageTooltip: false,
    }
  },
  computed: {
    ...mapState({
      companiesCustomSurvey: state => state.companiesCustomSurvey.data,
      companiesCustomSurveyLoading: state => state.companiesCustomSurvey.loading,
      mediaImage: state => state.companiesCustomSurvey.data.complete.mediaImage,
      title: state => state.companiesCustomSurvey.data.complete.title,
      text: state => state.companiesCustomSurvey.data.complete.text,
      showBtnForm: state => state.companiesCustomSurvey.data.complete.showBtnForm,
      btnLabel: state => state.companiesCustomSurvey.data.complete.btnLabel,
      btnLink: state => state.companiesCustomSurvey.data.complete.btnLink,
      guideToReviewSiteDelete: state => state.companiesCustomSurvey.data.guideToReviewSite.delete,
      guideToReviewSiteTitle: state => state.companiesCustomSurvey.data.guideToReviewSite.title,
      guideToReviewSiteDisplayLocation: state => state.companiesCustomSurvey.data.guideToReviewSite.displayLocation,
      tmpMediaImages: state => state.companiesMediaImage.images,
      loading: state => state.companiesMediaImage.loading || state.companiesCustomSurvey.loading,
    }),
    maxLength() {
      return {
        title: 140,
        text: 400,
        btnLabel: 35,
        btnLink: 1000,
        guideToReviewSiteTitle: 140,
      }
    },
    images() {
      return this.mediaImage ? [this.mediaImage] : []
    },
    guideToReviewSiteDisplayLocationOptions() {
      return [
        {
          id: 'survey_complete',
          value: 'survey_complete',
          text: this.$t('components.CompaniesSurveyTemplateForm.complete.formLabels.guideToReviewSiteDisplayLocation1'),
        },
        {
          id: 'store_and_survey_complete',
          value: 'store_and_survey_complete',
          text: this.$t('components.CompaniesSurveyTemplateForm.complete.formLabels.guideToReviewSiteDisplayLocation2'),
        },
      ]
    }
  },
  methods: {
    ...mapActions({
      changeAttribute: 'companiesCustomSurvey/changeAttribute',
      uploadMediaImages: 'companiesMediaImage/uploadMediaImages',
      deleteMediaImage: 'companiesMediaImage/deleteMediaImage',
      resetMediaImages: 'companiesMediaImage/resetMediaImages',
    }),
    labelTextFormat({ label, max = 0, required = true }) {
      let text = this.$t('components.CompaniesSurveyTemplateForm.complete.formLabels.' + label)
      if (max !== 0) {
        text += this.$t('components.CompaniesSurveyTemplateForm.labelCount', { current: this[label].length , max: max })
      }
      return required ? text + this.$t('labels.requiredLabel') : text
    },
    async onDropMediaImage(images) {
      await this.uploadMediaImages(images)
      await this.changeCompleteAttribute(this.tmpMediaImages[0], 'mediaImage')
      this.resetMediaImages()
    },
    onRemoveMediaImage(imageId) {
      this.deleteMediaImage(imageId)
      this.changeCompleteAttribute(null, 'mediaImage')
    },
    changeCompleteAttribute(value, key) {
      this.changeAttribute({ tab: 'complete', key: key, value: value })
    },
    changeGuideToReviewSiteAttribute(value, key) {
      this.changeAttribute({ tab: 'guideToReviewSite', key: key, value: value })
    },
    showHoverImageTooltip() {
      this.isShowHoverImageTooltip = true
    },
    hideHoverImageTooltip() {
      this.isShowHoverImageTooltip = false
    }
  }
}
</script>

<style lang="scss" scoped>
.box:not(:first-child) {
  margin-top: 32px;
}
.form {
  margin-top: 16px;
}
.info {
  margin-top: 16px;
}
</style>
